/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, Fragment, useMemo, useCallback, useState, useEffect } from "react";

// antd:
// import { LoadingOutlined } from "@ant-design/icons";
import { Form, Tree } from "antd";
import type { TreeDataNode, TreeProps } from "antd";

// eslint-disable-next-line node/no-missing-import
import type * as CSS from "csstype";

// My Components:
import AppIcons from "../react/AppIcons.tsx";
import SpanE from "../react/SpanE.tsx";
// Redux-hooks:
import { useAppSelector, useAppDispatch } from "../../libs/redux-hooks.ts";
// Reducers:
import { getLng, getDict, getAppState, getUser, changeAppState, getSafePtr } from "../../libs/reducer.ts";
// My functions
import fnc from "../../libs/fnc.ts";
// Types:
import type {TuniversalElementProps} from "../../types/Iuigen.d.ts";
import type {Tpermissions} from "../../types/easyTypes.ts";

const { rz } = window;

type Ttree = {
  allowDrop?:boolean,
  autoExpandParent?:boolean,
  defaultExpandParent?:boolean,
  defaultExpandAll?:boolean,
  checkable?:boolean,
  multiple?:boolean,
  selectable?:boolean,
  blockNode?:boolean,
  checkStrictly?:boolean,
  draggable?:boolean,
  showLine?:boolean, // Shows a connecting line
  showIcon?:boolean,
  height?:number
  // disabled?:boolean,
  // defaultCheckedKeys?:string[],
  // defaultExpandedKeys?:string[],
  // defaultSelectedKeys?:string[],
  // virtual?:boolean, // Disable virtual scroll when set to false
  // treeData?:Array<{ key, title, children, [disabled, selectable] }>,
  // expandedKeys?:string[],
  // selectedKeys?:string[]
};

const RZtree:FC<TuniversalElementProps> = ({attributes, children}) => {
  RZtree.displayName = "Tree";
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const dct = useAppSelector(getDict);
  const lng = useAppSelector(getLng);
  const safe = useAppSelector(getSafePtr);
  const rlng = (lng === "en" ? "ru" : "en");
  // const theme = useAppSelector(getTheme);

  const {rootAttributes, key, fullPath, type, content, hasitems} = attributes; // onClick, onChange, onRightClick, onHover, onLoad
  const appState = useAppSelector(getAppState(rootAttributes.key));
  const elState = appState[fullPath];
  // const {icon, iconClass, iconColor, color, backgroundColor} = elState.attributes;
  const rootNamespace = useMemo(() => (window[rootAttributes.key || rz.strMakeID(24)]), [rootAttributes.key]);

  const isParentForm = useMemo(() => {
    const parentPath = rz.strParentFolderName(fullPath || ""), parentState = appState[parentPath];
    return Boolean(parentState.name === "form");
  }, [appState, fullPath]);

  const permissions = useMemo<Tpermissions>(() => (fnc.checkPermissions(user, attributes)), [user, attributes]);

  const cmpType = useMemo<Ttree>(() => {
    const typ:Ttree = {allowDrop: false, autoExpandParent: false, blockNode: false, checkable: false, multiple: false, selectable: false};
    (type || "").replace((/(\s+|_|\|)/igm), " ").split(" ").forEach((tt) => {
      if (~["allowDrop", "allowdrop"].indexOf(tt)) { typ.allowDrop = true }
      else if (~["autoExpandParent", "autoexpandparent"].indexOf(tt)) { typ.autoExpandParent = true }
      else if (~["defaultExpandParent", "defaultexpandparent"].indexOf(tt)) { typ.defaultExpandParent = true }
      else if (~["defaultExpandAll", "defaultexpandall"].indexOf(tt)) { typ.defaultExpandAll = true }
      else if (~["blockNode", "blocknode"].indexOf(tt)) { typ.blockNode = true }
      else if (tt === "checkable") { typ.checkable = true }
      else if (tt === "multiple") { typ.multiple = true }
      else if (tt === "selectable") { typ.selectable = true }
      else if (tt === "draggable") { typ.draggable = true }
      else if (~["checkStrictly", "checkstrictly"].indexOf(tt)) { typ.checkStrictly = true }
      else if (~["showLine", "showline"].indexOf(tt)) { typ.showLine = true }
      else if (~["showIcon", "showicon"].indexOf(tt)) { typ.showIcon = true }
    });

    if (attributes?.allowDrop || attributes?.allowdrop) { typ.allowDrop = Boolean(attributes.allowDrop === "true" || attributes.allowdrop === "true") }
    if (attributes?.autoExpandParent || attributes?.autoexpandparent) { typ.autoExpandParent = Boolean(attributes.autoExpandParent === "true" || attributes.autoexpandparent === "true") }
    if (attributes?.defaultExpandParent || attributes?.defaultexpandparent) { typ.defaultExpandParent = Boolean(attributes.defaultExpandParent === "true" || attributes.defaultexpandparent === "true") }
    if (attributes?.defaultExpandAll || attributes?.defaultexpandall) { typ.defaultExpandAll = Boolean(attributes.defaultExpandAll === "true" || attributes.defaultexpandall === "true") }
    if (attributes?.blockNode || attributes?.blocknode) { typ.blockNode = Boolean(attributes.blockNode === "true" || attributes.blocknode === "true") }
    if (attributes?.checkable) { typ.checkable = Boolean(attributes.checkable === "true") }
    if (attributes?.multiple) { typ.multiple = Boolean(attributes.multiple === "true") }
    if (attributes?.selectable) { typ.selectable = Boolean(attributes.selectable === "true") }
    if (attributes?.draggable) { typ.draggable = Boolean(attributes.draggable === "true") }
    if (attributes?.checkStrictly || attributes?.checkstrictly) { typ.checkStrictly = Boolean(attributes.checkStrictly === "true" || attributes.checkstrictly === "true") }
    if (attributes?.showLine || attributes?.showline) { typ.showLine = Boolean(attributes.showLine === "true" || attributes.showline === "true") }
    if (attributes?.showIcon || attributes?.showicon) { typ.showIcon = Boolean(attributes.showIcon === "true" || attributes.showicon === "true") }
    if (attributes?.height) { typ.height = Number(attributes.height) }

    const specialParams = elState?.specialParams || {};

    if (specialParams.showLine !== undefined) { typ.showLine = Boolean(specialParams.showLine); }
    if (specialParams.showIcon !== undefined) { typ.showIcon = Boolean(specialParams.showIcon); }
    return typ;
  }, [type, attributes, elState?.specialParams]);

  const [autoExpandParent, setAutoExpandParent] = useState(Boolean(cmpType.autoExpandParent));

  const clsu = useMemo<Array<string>>(() => {
    const userClss = rz.objClone(elState?.userClass || []);
    // if ((elState?.disabled || permissions.disabled === true) && !~userClss.indexOf("uk-disabled")) { userClss.push("uk-disabled") }
    return userClss;
  }, [elState?.userClass]);

  const styl = useMemo<CSS.Properties>(() => {
    const est = {...elState.userStyle};
    if ((elState?.visible === false || permissions.visible === false) && est.display !== "none") { est.display = "none" }
    else if ((elState?.visible === true && permissions.visible !== false) && est.display === "none") { est.display = undefined }
    return est;
  }, [elState?.userStyle, elState?.visible, permissions]);

  // eslint-disable-next-line max-len
  const title = useMemo<string | undefined>(() => (elState.attributes["title_" + lng] || elState.attributes.title || elState.attributes["title_" + rlng] || attributes["title_" + lng] || attributes.title || attributes["title_" + rlng]), [elState.attributes, lng, rlng, attributes]);
  // eslint-disable-next-line max-len
  const title2 = useMemo<string | undefined>(() => (elState?.attributes["title2_" + lng] || elState?.attributes.title2 || elState?.attributes["title2_" + rlng] || attributes["title2_" + lng] || attributes.title2 || attributes["title2_" + rlng]), [elState?.attributes, lng, rlng, attributes]);
  const tooltip = useMemo<string | undefined>(() => (elState.attributes["tooltip_" + lng] || elState.attributes.tooltip || elState.attributes["tooltip_" + rlng]), [elState.attributes, lng, rlng]);

  const items = useMemo<Array<TreeDataNode>>(() => {
    let itms:Array<TreeDataNode> = [];

    const getSubContent = (cnt) => {
      if (!cnt || !rz.isArray(cnt) || !cnt.length) { return undefined }
      return cnt.map((mm) => {
        const perm1 = fnc.checkPermissions(user, {...mm?.attributes, rootAttributes: {key: rootAttributes.key}}), state1 = appState[mm?.attributes?.fullPath || mm?.fullPath];
        const key1 = mm.key || rz.strMakeID(16);
        let icon1:React.ReactElement | undefined;
        // eslint-disable-next-line max-len
        if (mm?.attributes?.icon) { icon1 = (<AppIcons key={key1 + "-icon"} icopath={mm.attributes.icon} size={mm.attributes?.iconSize} attrs={{className: mm.attributes?.iconClass || "rz-margin-right-5", color: mm.attributes?.iconColor, size: mm.attributes?.iconSize}} />) }
        else if (mm?.icon) { icon1 = (<AppIcons key={key1 + "-icon"} icopath={mm.icon} size={mm?.iconSize} attrs={{className: mm?.iconClass || "rz-margin-right-5", color: mm?.iconColor, size: mm?.iconSize}} />) }
        if (mm?.attributes?.img) {
          icon1 = (
            <img key={key1 + "-img"} src={mm.attributes.img} style={mm.attributes.imgStyle ? rz.parseCSSText(mm.attributes?.imgStyle) : undefined} className={mm.attributes.imgClass ? mm.attributes?.imgClass : undefined} />
          );
        }
        else if (mm?.img) {
          icon1 = (
            <SpanE key={key1 + "-span-i"} style={mm.imgStyle ? rz.parseCSSText(mm.imgStyle) : undefined}>
              <img key={key1 + "-img"} src={mm.img} style={mm.imgStyle ? rz.parseCSSText(mm.imgStyle) : undefined} className={mm.imgClass ? mm.imgClass : undefined} />
            </SpanE>
          );
        }

        const title1 = (
          rz.isString(mm) ? mm : (mm?.attributes ? (
            mm.attributes["title_" + lng] || mm.attributes.title || mm.attributes["title_" + rlng] || mm.attributes["label_" + lng] || mm.attributes.label || mm.attributes["label_" + rlng] || ""
          ) : (
            mm["title_" + lng] || mm.title || mm["title_" + rlng] || mm["label_" + lng] || mm.label || mm["label_" + rlng] || ""
          )
          ));
        const titleNode = (
          <SpanE key={key1 + "-title"} className={(mm.attributes && mm.attributes.class ? mm.attributes.class : (mm.class ? mm.class : undefined))}
            style={(mm.attributes && mm.attributes.style ? rz.parseCSSText(mm.attributes.style) : (mm.style ? rz.parseCSSText(mm.style) : undefined))}
            uk-tooltip={(mm.attributes && mm.attributes.tooltip ? mm.attributes.tooltip : (mm.tooltip ? mm.tooltip : undefined))}
          >
            {title1}
          </SpanE>
        );
        return {
          key: key1,
          icon: icon1, title: titleNode,
          disabled: (state1?.disabled || perm1.disabled === true || mm.disabled), disableCheckbox: (state1?.disabled || perm1.disabled === true || mm.disableCheckbox),
          checkable: (rz.isUndefinedOrNull(mm.checkable) ? undefined : mm.checkable),
          selectable: (rz.isUndefinedOrNull(mm.selectable) ? undefined : mm.selectable),
          children: getSubContent(mm?.children || mm?.content)
        };
      });
    };

    const sitms = elState?.items;
    if (elState?.items) {
      if (rz.isArray(elState.items)) { itms = getSubContent(elState.items) }
      else if (rz.isString(sitms)) { itms = (sitms.split("|")).map((value) => ({value, title: value, key: rz.strMakeID(16)})) }
    }
    else if (hasitems && content && rz.isArray(content)) {
      itms = getSubContent(content);
    }
    return itms;
  }, [appState, content, elState.items, hasitems, lng, rlng, rootAttributes.key, user]);

  const allKeys = useMemo(() => {
    const allkeys:Array<string> = [];
    const pushChildrenKeys = (chldrn) => {
      chldrn.forEach((rr) => {
        allkeys.push(rr.key || rr.value);
        if (rr.children && rr.children.length) { pushChildrenKeys(rr.children) }
      });
    };
    (elState.items || []).forEach((rr) => {
      allkeys.push(rr.key || rr.value);
      if (rr.children && rr.children.length) { pushChildrenKeys(rr.children) }
    });
    return allkeys;
  }, [elState.items]);

  const onSelect = useCallback((selectedKeys, info) => {
    if (fnc.config?.isDev) console.log({action: "<Component key='" + key + "'> -> onSelect()", selectedKeys, info});
    dispatch(changeAppState({appkey: rootAttributes.key, fullPath, newState: {...elState, specialParams: {...elState.specialParams, selectedKeys}}}));

    if (rootNamespace && (attributes.onSelect || attributes.onselect) && rootNamespace[attributes.onSelect || attributes.onselect]) {
      const clientFNC = rootNamespace[attributes.onSelect || attributes.onselect];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, selectedKeys, info, safe}).then((ret) => {
          if (rz.isUndefinedOrNull(ret)) { return }
          if (ret?.err) { console.log(ret.err) }
          if (ret && ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
        });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, selectedKeys, info, safe}); if (rz.isUndefinedOrNull(ret)) { return }
        if (ret?.err) { console.log(ret.err) }
        if (ret && ret?.state?.fullPath && ret?.state?.newState) {
          dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
        }
      }
    }
  }, [key, dispatch, rootAttributes.key, fullPath, elState, rootNamespace, attributes, user, lng, dct, safe]);

  const onCheck = useCallback((checkedKeys, info) => {
    if (fnc.config?.isDev) console.log({action: "<Component key='" + key + "'> -> onCheck()", checkedKeys, info});
    dispatch(changeAppState({appkey: rootAttributes.key, fullPath, newState: {...elState, specialParams: {...elState.specialParams, checkedKeys, halfCheckedKeys: info?.halfCheckedKeys || []}}}));

    if (rootNamespace && (attributes.onCheck || attributes.oncheck) && rootNamespace[attributes.onCheck || attributes.oncheck]) {
      const clientFNC = rootNamespace[attributes.onCheck || attributes.oncheck];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, checkedKeys, info, safe}).then((ret) => {
          if (rz.isUndefinedOrNull(ret)) { return }
          if (ret?.err) { console.log(ret.err) }
          if (ret && ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
        });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, checkedKeys, info, safe}); if (rz.isUndefinedOrNull(ret)) { return }
        if (ret?.err) { console.log(ret.err) }
        if (ret && ret?.state?.fullPath && ret?.state?.newState) {
          dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
        }
      }
    }
  }, [key, dispatch, rootAttributes.key, fullPath, elState, rootNamespace, attributes, user, lng, dct, safe]);

  const onExpand = useCallback((expandedKeys) => {
    if (fnc.config?.isDev) console.log({action: "<Component key='" + key + "'> -> onExpand()", expandedKeys});
    setAutoExpandParent(false);
    dispatch(changeAppState({appkey: rootAttributes.key, fullPath, newState: {...elState, specialParams: {...elState.specialParams, expandedKeys}}}));

    if (rootNamespace && (attributes.onExpand || attributes.onexpand) && rootNamespace[attributes.onExpand || attributes.onexpand]) {
      const clientFNC = rootNamespace[attributes.onExpand || attributes.onexpand];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, expandedKeys, safe}).then((ret) => {
          if (rz.isUndefinedOrNull(ret)) { return }
          if (ret?.err) { console.log(ret.err) }
          if (ret && ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
        });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, expandedKeys, safe}); if (rz.isUndefinedOrNull(ret)) { return }
        if (ret?.err) { console.log(ret.err) }
        if (ret && ret?.state?.fullPath && ret?.state?.newState) {
          dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
        }
      }
    }
  }, [key, dispatch, rootAttributes.key, fullPath, elState, rootNamespace, attributes, user, lng, dct, safe]);

  const onDrop = useCallback((info) => {
    if (fnc.config?.isDev) console.log({action: "<Component key='" + key + "'> -> onDrop()", info});

    if (rootNamespace && (attributes.onDrop || attributes.ondrop) && rootNamespace[attributes.onDrop || attributes.ondrop]) {
      const clientFNC = rootNamespace[attributes.onDrop || attributes.ondrop];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, info, safe}).then((ret) => {
          if (rz.isUndefinedOrNull(ret)) { return }
          if (ret?.err) { console.log(ret.err) }
          if (ret && ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
        });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, info, safe});
        if (rz.isUndefinedOrNull(ret)) { return }
        if (ret?.err) { console.log(ret.err) }
        if (ret && ret?.state?.fullPath && ret?.state?.newState) {
          dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
        }
      }
    }
  }, [key, dispatch, rootAttributes.key, rootNamespace, attributes, user, lng, dct, safe]);

  const wrapperForm = useCallback((mainComponent:React.ReactNode) => {
    if (isParentForm) {
      const frmItemProps:{[key:string]:any} = {
        key: key + "-formitem", label: (<span className="rz-txt-std">{title2}</span>), className: "rz-width-expand",
        style: ((elState?.visible === false || permissions.visible === false) ? {display: "none"} : undefined)
      };
      return (
        <Form.Item {...frmItemProps}>
          {mainComponent}
        </Form.Item>
      )
    }
    return mainComponent;
  }, [isParentForm, title2, key, elState?.visible, permissions.visible]);

  return wrapperForm(
    <Tree
      key={key} className={clsu.join(" ")} uk-tooltip={tooltip}
      // style={styl}
      rootStyle={styl}
      treeData={items}
      autoExpandParent={autoExpandParent}
      defaultExpandParent={cmpType.defaultExpandParent}
      defaultExpandAll={cmpType.defaultExpandAll}
      checkable={cmpType.checkable}
      multiple={cmpType.multiple}
      selectable={cmpType.selectable}
      blockNode={cmpType.blockNode}
      checkStrictly={cmpType.checkStrictly}
      draggable={cmpType.draggable}
      showLine={cmpType.showLine}
      showIcon={cmpType.showIcon}
      defaultExpandedKeys={elState?.specialParams?.defaultExpandedKeys}
      expandedKeys={cmpType.defaultExpandAll ? allKeys : elState?.specialParams?.expandedKeys}
      defaultCheckedKeys={elState?.specialParams?.defaultCheckedKeys}
      defaultSelectedKeys={elState?.specialParams?.defaultSelectedKeys}
      checkedKeys={elState?.specialParams?.checkedKeys}
      selectedKeys={elState?.specialParams?.selectedKeys}
      height={cmpType.height}
      onDrop={onDrop}
      onSelect={onSelect}
      onCheck={onCheck}
      onExpand={onExpand}
      disabled={elState?.disabled || permissions.disabled === true}
    />
  );
}

export default RZtree;
/* eslint-enable @typescript-eslint/no-unused-vars */
